import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Phone, Mail, User, MapPin, CreditCard, MessageSquare } from "lucide-react";
import type { CreateArtistDetailData, UpdateArtistDetailData, ArtistDetail } from "@/hooks/useArtistDetails";

interface ArtistFormProps {
  editingArtist?: ArtistDetail | null;
  onSubmit: (data: CreateArtistDetailData | UpdateArtistDetailData) => Promise<boolean>;
  onCancel: () => void;
}

export function ArtistForm({ editingArtist, onSubmit, onCancel }: ArtistFormProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState<CreateArtistDetailData>({
    full_name: "",
    email_address: "",
    phone_number: "",
    whatsapp_number: "",
    full_address: "",
    city_state: "",
    pin_code: "",
    bank_account_details: "",
    upi_id: "",
    availability: "email",
    status: "pending"
  });
  const [whatsappSame, setWhatsappSame] = useState(true);
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingArtist) {
      setFormData({
        full_name: editingArtist.full_name || "",
        email_address: editingArtist.email_address || "",
        phone_number: editingArtist.phone_number || "",
        whatsapp_number: editingArtist.whatsapp_number || "",
        full_address: editingArtist.full_address || "",
        city_state: editingArtist.city_state || "",
        pin_code: editingArtist.pin_code || "",
        bank_account_details: editingArtist.bank_account_details || "",
        upi_id: editingArtist.upi_id || "",
        availability: editingArtist.availability || "email",
        status: editingArtist.status || "pending"
      });
      
      // Check if WhatsApp number is same as phone
      setWhatsappSame(
        editingArtist.whatsapp_number === editingArtist.phone_number ||
        editingArtist.whatsapp_number === null
      );

      // Parse availability string to array (handle both single and multiple values)
      if (editingArtist.availability) {
        if (editingArtist.availability.includes(',')) {
          setSelectedAvailability(editingArtist.availability.split(',').map(s => s.trim()));
        } else {
          setSelectedAvailability([editingArtist.availability]);
        }
      } else {
        setSelectedAvailability([]);
      }
    }
  }, [editingArtist]);

  const handleInputChange = (field: keyof CreateArtistDetailData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWhatsAppChange = (checked: boolean) => {
    setWhatsappSame(checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        whatsapp_number: prev.phone_number
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        whatsapp_number: ""
      }));
    }
  };

  const handleAvailabilityChange = (method: string, checked: boolean) => {
    if (checked) {
      setSelectedAvailability(prev => [...prev, method]);
    } else {
      setSelectedAvailability(prev => prev.filter(m => m !== method));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.full_name.trim()) {
      toast({ title: "Error", description: "Full Name is required", variant: "destructive" });
      return;
    }
    if (!formData.email_address.trim()) {
      toast({ title: "Error", description: "Email Address is required", variant: "destructive" });
      return;
    }
    if (!formData.phone_number.trim()) {
      toast({ title: "Error", description: "Phone Number is required", variant: "destructive" });
      return;
    }
    if (!formData.full_address.trim()) {
      toast({ title: "Error", description: "Full Address is required", variant: "destructive" });
      return;
    }
    if (!formData.city_state.trim()) {
      toast({ title: "Error", description: "City & State is required", variant: "destructive" });
      return;
    }
    if (!formData.pin_code.trim()) {
      toast({ title: "Error", description: "Pin Code is required", variant: "destructive" });
      return;
    }
    if (!formData.upi_id.trim()) {
      toast({ title: "Error", description: "UPI ID is required", variant: "destructive" });
      return;
    }
    if (selectedAvailability.length === 0) {
      toast({ title: "Error", description: "Please select at least one contact method", variant: "destructive" });
      return;
    }

    try {
      setLoading(true);
      
      // Prepare data for submission
      const submitData = {
        ...formData,
        whatsapp_number: whatsappSame ? formData.phone_number : formData.whatsapp_number,
        availability: selectedAvailability.join(', ')
      };

      const success = await onSubmit(submitData);
      
      if (success) {
        toast({ 
          title: "Success", 
          description: editingArtist ? "Artist updated successfully" : "Artist registered successfully" 
        });
        onCancel();
      }
    } catch (error) {
      console.error("Error submitting artist form:", error);
      toast({ 
        title: "Error", 
        description: "Failed to save artist details. Please try again.", 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <User className="h-5 w-5" />
          Basic Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="full_name" className="text-sm font-medium">
              Full Name 👤 *
            </Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={(e) => handleInputChange("full_name", e.target.value)}
              placeholder="Enter full name"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email_address" className="text-sm font-medium">
              Email Address 📧 *
            </Label>
            <Input
              id="email_address"
              type="email"
              value={formData.email_address}
              onChange={(e) => handleInputChange("email_address", e.target.value)}
              placeholder="Enter email address"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="phone_number" className="text-sm font-medium">
              Phone Number 📱 *
            </Label>
            <Input
              id="phone_number"
              type="tel"
              value={formData.phone_number}
              onChange={(e) => handleInputChange("phone_number", e.target.value)}
              placeholder="Enter phone number"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              WhatsApp Number (if different) *
            </Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="whatsapp_same"
                  checked={whatsappSame}
                  onCheckedChange={handleWhatsAppChange}
                />
                <Label htmlFor="whatsapp_same" className="text-sm">Same as phone</Label>
              </div>
              {!whatsappSame && (
                <Input
                  type="tel"
                  value={formData.whatsapp_number}
                  onChange={(e) => handleInputChange("whatsapp_number", e.target.value)}
                  placeholder="Enter WhatsApp number"
                  required
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Address Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Address Information
        </h3>
        
        <div className="space-y-2">
          <Label htmlFor="full_address" className="text-sm font-medium">
            Full Address (for pickup of finished orders) 🏠 *
          </Label>
          <Textarea
            id="full_address"
            value={formData.full_address}
            onChange={(e) => handleInputChange("full_address", e.target.value)}
            placeholder="Enter complete address"
            rows={3}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="city_state" className="text-sm font-medium">
              City & State *
            </Label>
            <Input
              id="city_state"
              value={formData.city_state}
              onChange={(e) => handleInputChange("city_state", e.target.value)}
              placeholder="e.g., Mumbai, Maharashtra"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="pin_code" className="text-sm font-medium">
              Pin Code *
            </Label>
            <Input
              id="pin_code"
              value={formData.pin_code}
              onChange={(e) => handleInputChange("pin_code", e.target.value)}
              placeholder="Enter pin code"
              required
            />
          </div>
        </div>
      </div>

      {/* Payment & Commission */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Payment & Commission
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="bank_account_details" className="text-sm font-medium">
              Bank Account Details (Name, Account Number, IFSC) 💳
            </Label>
            <Textarea
              id="bank_account_details"
              value={formData.bank_account_details}
              onChange={(e) => handleInputChange("bank_account_details", e.target.value)}
              placeholder="Enter bank account details"
              rows={3}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="upi_id" className="text-sm font-medium">
              UPI ID (for faster payouts) 📲 *
            </Label>
            <Input
              id="upi_id"
              value={formData.upi_id}
              onChange={(e) => handleInputChange("upi_id", e.target.value)}
              placeholder="Enter UPI ID"
              required
            />
          </div>
        </div>
      </div>

      {/* Agreement & Communication */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Agreement & Communication
        </h3>
        
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Availability (Best way to contact) *
          </Label>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="availability_email"
                checked={selectedAvailability.includes("email")}
                onCheckedChange={(checked) => handleAvailabilityChange("email", checked as boolean)}
              />
              <Label htmlFor="availability_email" className="text-sm flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="availability_whatsapp"
                checked={selectedAvailability.includes("whatsapp")}
                onCheckedChange={(checked) => handleAvailabilityChange("whatsapp", checked as boolean)}
              />
              <Label htmlFor="availability_whatsapp" className="text-sm flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                WhatsApp
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="availability_phone"
                checked={selectedAvailability.includes("phone")}
                onCheckedChange={(checked) => handleAvailabilityChange("phone", checked as boolean)}
              />
              <Label htmlFor="availability_phone" className="text-sm flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Phone
              </Label>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Select all preferred contact methods. You can choose multiple options.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="status" className="text-sm font-medium">
            Initial Status *
          </Label>
          <Select value={formData.status} onValueChange={(value) => handleInputChange("status", value as any)}>
            <SelectTrigger>
              <SelectValue placeholder="Select initial status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? "Saving..." : editingArtist ? "Update Artist" : "Register Artist"}
        </Button>
      </div>
    </form>
  );
}
